// Minhas tarefas (PO1-2194, A7): tarefas geradas pelo motor de alertas para o
// usuário, no estilo do card "Tasks" do black-dashboard (card-tasks). Uma
// instância por produto (prop `produto`, montada por makeAlertasItens).
//
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import { AUTOPILOT_COLORS, autopilotCardStyle } from "../../../components/autopilot/styles";
import { useToast } from "../../../hooks/toast";
import { concluirTarefa, listarTarefas } from "../../../services/alertasApi";
import { PRODUTO_LABEL, abrirLink, formatarData, formatarDataHora, mensagemErro } from "../alertasUtils";
import "./styles.css";

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem", marginBottom: 0 };

function prazoVencido(prazo) {
  if (!prazo) return false;
  const d = new Date(prazo);
  if (isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

export default function MinhasTarefas({ produto }) {
  const history = useHistory();
  const { addToast } = useToast();
  const [status, setStatus] = useState("ABERTA");
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [concluindo, setConcluindo] = useState({});

  const flash = useCallback(
    (type, description) =>
      addToast({ place: "tr", type, title: type === "success" ? "OK" : "Atenção", description }),
    [addToast],
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const data = await listarTarefas({ produto, status });
      setItens((data && data.items) || []);
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao carregar as tarefas."));
    } finally {
      setCarregando(false);
    }
  }, [produto, status, flash]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const concluir = async (tarefa) => {
    setConcluindo((p) => ({ ...p, [tarefa.id]: true }));
    try {
      await concluirTarefa(tarefa.id);
      // Sai da lista de abertas na hora; a recarga confirma com o servidor.
      setItens((prev) => prev.filter((t) => t.id !== tarefa.id));
      flash("success", `Tarefa "${tarefa.nome}" concluída.`);
      carregar();
    } catch (e) {
      flash("danger", mensagemErro(e, "Falha ao concluir a tarefa."));
    } finally {
      setConcluindo((p) => ({ ...p, [tarefa.id]: false }));
    }
  };

  const abertas = status === "ABERTA";

  return (
    <div className="content">
      <PageHero
        eyebrow={PRODUTO_LABEL[produto] || "Alertas"}
        title="Minhas tarefas"
        description="Pendências que o Hub gerou para você resolver. Marque como concluída quando terminar."
        actions={<PageHeroRefreshButton onClick={carregar} loading={carregando} />}
      />

      <Card
        className="card-tasks card-everlog-shell alertas-tarefas"
        style={{
          ...autopilotCardStyle,
          background: "var(--everlog-card-hero-bg)",
          marginTop: "1.25rem",
          height: "auto",
        }}
      >
        <CardHeader style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
          <h6 className="title d-inline" style={{ color: AUTOPILOT_COLORS.title, margin: 0 }}>
            Tarefas ({itens.length})
          </h6>
          <ButtonGroup>
            <Button size="sm" color="info" outline={!abertas} onClick={() => setStatus("ABERTA")}>
              Abertas
            </Button>
            <Button size="sm" color="info" outline={abertas} onClick={() => setStatus("CONCLUIDA")}>
              Concluídas
            </Button>
          </ButtonGroup>
        </CardHeader>
        <CardBody>
          <div className="table-full-width table-responsive" style={{ maxHeight: "none" }}>
            <Table>
              <tbody>
                {itens.map((t) => {
                  const vencido = abertas && prazoVencido(t.prazo);
                  const idAcao = `tarefa-link-${t.id}`;
                  return (
                    <tr key={t.id}>
                      <td style={{ width: 48 }}>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={!abertas}
                              disabled={!abertas || !!concluindo[t.id]}
                              aria-label={`Concluir ${t.nome}`}
                              onChange={() => concluir(t)}
                            />
                            <span className="form-check-sign">
                              <span className="check" />
                            </span>
                          </Label>
                        </FormGroup>
                      </td>
                      <td>
                        <p className="title" style={{ color: AUTOPILOT_COLORS.title, marginBottom: 2 }}>
                          {t.nome}
                        </p>
                        <p style={{ color: AUTOPILOT_COLORS.textSoft, fontSize: "0.84rem", marginBottom: 2 }}>{t.mensagem}</p>
                        <p style={mutedStyle}>
                          {t.prazo ? (
                            <span style={{ color: vencido ? AUTOPILOT_COLORS.statusDanger : undefined, marginRight: 12 }}>
                              Prazo: {formatarData(t.prazo)}
                              {vencido ? " (vencido)" : ""}
                            </span>
                          ) : null}
                          <span style={{ marginRight: 12 }}>Criada em {formatarDataHora(t.criadoEm)}</span>
                          {!abertas && t.concluidoEm ? <span>Concluída em {formatarDataHora(t.concluidoEm)}</span> : null}
                        </p>
                      </td>
                      <td className="td-actions text-right" style={{ width: 56 }}>
                        {t.link ? (
                          <>
                            <Button color="link" id={idAcao} type="button" onClick={() => abrirLink(t.link, history)}>
                              <i className="tim-icons icon-double-right" />
                            </Button>
                            <UncontrolledTooltip delay={0} target={idAcao}>
                              Abrir
                            </UncontrolledTooltip>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {itens.length === 0 && !carregando ? (
                  <tr>
                    <td colSpan={3} style={{ color: AUTOPILOT_COLORS.muted, padding: "1.5rem", textAlign: "center" }}>
                      {abertas ? "Nenhuma tarefa aberta. Tudo em dia." : "Nenhuma tarefa concluída ainda."}
                    </td>
                  </tr>
                ) : null}
                {carregando && itens.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: "1.5rem", textAlign: "center" }}>
                      <Spinner size="sm" />
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
